import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Eye, Sparkles, Loader2, AlertCircle, ExternalLink, RefreshCw, Rocket, Target, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import organicFetch from '../lib/organicFetch';

// Viral Boost — which viral Page videos are worth paying to boost as the TOP of
// a sales funnel, and which went viral for reasons that won't find buyers (a
// joke, "where are the gloves?"). Claude reads caption + top comments + watch
// time per video (meta-organic boost-analyze); results cache in Neon
// viral_boost_recs so the tab opens instantly after the first pass.

const BRANDS = [{ slug: 'crabplace', name: 'Crab Place' }, { slug: 'crabandcruise', name: 'Crab & Cruise' }];
const ghostBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 12px' };
const primaryBtn = { background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '7px 14px' };
const VERDICT = {
  boost: { label: 'Boost it', bg: '#e6f6ec', fg: '#1a7a43', border: '#b9e4c8' },
  maybe: { label: 'Maybe', bg: '#fff6e6', fg: '#9a5b00', border: '#ffd9a8' },
  skip: { label: 'Skip', bg: '#f4f5f7', fg: '#6b7280', border: '#e2e5e9' },
};
const BATCH = 3;   // videos per boost-analyze call (sync-function time limit)
const TOP_N = 30;  // analyze the 30 most-viewed

function fmtViews(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}K`;
  return String(n || 0);
}
function fmtDate(d) { if (!d) return ''; try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } }

async function post(body) {
  const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const text = await r.text();
  try { return JSON.parse(text); } catch { return { error: `Server returned ${r.status}` }; }
}

export default function ViralBoost() {
  const [brand, setBrand] = useState('crabplace');
  const [videos, setVideos] = useState(null);
  const [state, setState] = useState('idle'); // idle|loading|ok|needsPermission|error
  const [msg, setMsg] = useState(null);
  const [recs, setRecs] = useState({});
  const [running, setRunning] = useState(null); // null | { done, total }
  const [busyIds, setBusyIds] = useState({});
  const [filter, setFilter] = useState('boost');
  const [runErr, setRunErr] = useState(null);
  const cancelRef = useRef(false);

  const load = useCallback(async () => {
    setState('loading'); setMsg(null); setRecs({}); setRunErr(null);
    const d = await post({ action: 'videos', brand });
    if (d.needsPermission) { setState('needsPermission'); setMsg(d.message); setVideos([]); return; }
    if (d.error) { setState('error'); setMsg(d.error); setVideos([]); return; }
    const top = (d.videos || []).slice(0, TOP_N);
    setVideos(top); setState('ok');
    if (top.length) {
      const r = await post({ action: 'boost-recs', brand, ids: top.map(v => v.id) });
      setRecs(r.recs || {});
    }
  }, [brand]);

  useEffect(() => { cancelRef.current = true; setRunning(null); load(); }, [load]);

  async function analyze(list) {
    cancelRef.current = false; setRunErr(null);
    setRunning({ done: 0, total: list.length });
    for (let i = 0; i < list.length; i += BATCH) {
      if (cancelRef.current) break;
      const chunk = list.slice(i, i + BATCH);
      setBusyIds(prev => { const n = { ...prev }; chunk.forEach(v => { n[v.id] = true; }); return n; });
      const d = await post({ action: 'boost-analyze', brand, videos: chunk });
      if (d.error) setRunErr(d.error);
      setRecs(prev => ({ ...prev, ...(d.recs || {}) }));
      setBusyIds(prev => { const n = { ...prev }; chunk.forEach(v => { delete n[v.id]; }); return n; });
      setRunning({ done: Math.min(i + BATCH, list.length), total: list.length });
    }
    setRunning(null);
  }

  const unscored = (videos || []).filter(v => !recs[v.id]);
  const counts = { boost: 0, maybe: 0, skip: 0, new: unscored.length };
  (videos || []).forEach(v => { const r = recs[v.id]; if (r) counts[r.verdict] += 1; });
  const shown = (videos || [])
    .filter(v => (filter === 'new' ? !recs[v.id] : filter === 'all' ? true : recs[v.id] && recs[v.id].verdict === filter))
    .sort((a, b) => ((recs[b.id] || {}).score || -1) - ((recs[a.id] || {}).score || -1) || b.views - a.views);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginRight: 4 }}>Page</span>
        {BRANDS.map(b => (
          <button key={b.slug} onClick={() => setBrand(b.slug)} disabled={!!running}
            style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat', background: brand === b.slug ? 'var(--navy)' : '#fff', color: brand === b.slug ? '#fff' : 'var(--navy)', border: `1px solid ${brand === b.slug ? 'var(--navy)' : 'var(--gray-200)'}` }}>
            {b.name}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 14, lineHeight: 1.5, maxWidth: 900 }}>
        Which viral videos are worth money as the <strong>top of the funnel</strong>. Claude reads each video's caption, top comments and watch time and asks one question: were the views about the food, or about something else (a joke, "where are the gloves?")? Boost the winners, save the <strong>50%+ viewers and link clickers</strong> as audiences, then retarget them with the supporting videos listed under each one.
      </div>

      <BoostLauncher />

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Recommended viral boosts</span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {running
              ? <>
                  <span style={{ fontSize: 12, color: 'var(--gray-500)' }}><Loader2 size={13} className="spin" style={{ verticalAlign: 'middle', marginRight: 4 }} />Scoring {running.done} of {running.total}…</span>
                  <button style={ghostBtn} onClick={() => { cancelRef.current = true; }}>Stop</button>
                </>
              : <>
                  {state === 'ok' && unscored.length > 0 && (
                    <button style={primaryBtn} onClick={() => analyze(unscored)}><Sparkles size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Score {unscored.length} video{unscored.length === 1 ? '' : 's'}</button>
                  )}
                  {state === 'ok' && unscored.length === 0 && videos && videos.length > 0 && (
                    <button style={ghostBtn} onClick={() => analyze(videos)} title="Re-read comments and watch time for every video">Re-score all</button>
                  )}
                  <button style={ghostBtn} onClick={load}><RefreshCw size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Refresh</button>
                </>}
          </span>
        </div>
        <div className="card-body" style={{ padding: 16 }}>
          {state === 'loading' && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading your top videos…</div>}
          {state === 'needsPermission' && (
            <div style={{ fontSize: 12, color: '#9a5b00', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 6, padding: 12 }}>
              <AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> {msg}
            </div>
          )}
          {state === 'error' && <div style={{ fontSize: 12, color: '#c33' }}>{msg}</div>}
          {runErr && <div style={{ fontSize: 12, color: '#c33', marginBottom: 10 }}>Scoring hit an error: {runErr}</div>}

          {state === 'ok' && videos && videos.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                {[{ k: 'boost', label: `Boost it (${counts.boost})` }, { k: 'maybe', label: `Maybe (${counts.maybe})` }, { k: 'skip', label: `Skip (${counts.skip})` }, { k: 'new', label: `Not scored (${counts.new})` }, { k: 'all', label: 'All' }].map(f => (
                  <button key={f.k} onClick={() => setFilter(f.k)}
                    style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 14, cursor: 'pointer', fontFamily: 'Montserrat', background: filter === f.k ? 'var(--orange)' : '#fff', color: filter === f.k ? '#fff' : 'var(--gray-500)', border: `1px solid ${filter === f.k ? 'var(--orange)' : 'var(--gray-200)'}` }}>
                    {f.label}
                  </button>
                ))}
              </div>
              {shown.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                  {counts.new === videos.length ? `None of your top ${videos.length} videos have been scored yet. Click "Score ${videos.length} videos" above (about 15 seconds per 3).` : 'Nothing in this group.'}
                </div>
              )}
              {shown.map(v => <BoostRow key={v.id} v={v} rec={recs[v.id]} busy={!!busyIds[v.id]} disabled={!!running} onRescore={() => analyze([v])} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BoostRow({ v, rec, busy, disabled, onRescore }) {
  const vs = rec ? VERDICT[rec.verdict] || VERDICT.maybe : null;
  const watchPct = rec && rec.avgWatchSec != null && v.lengthSec ? Math.round((rec.avgWatchSec / v.lengthSec) * 100) : null;
  return (
    <div style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
      <a href={v.permalink || '#'} target="_blank" rel="noreferrer" style={{ flexShrink: 0, lineHeight: 0 }}>
        {v.thumb
          ? <img src={v.thumb} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200)', background: '#000' }} />
          : <span style={{ width: 96, height: 96, borderRadius: 8, background: '#eef0f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Eye size={20} color="#9aa0a6" /></span>}
      </a>
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {vs && <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 10, background: vs.bg, color: vs.fg, border: `1px solid ${vs.border}` }}>{vs.label} · {rec.score}</span>}
          {rec && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: '#eaf1f6', color: 'var(--navy)' }}>{rec.category}</span>}
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520 }}>{v.title}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
          <span><Eye size={11} style={{ verticalAlign: 'middle' }} /> {fmtViews(v.views)} views</span>
          {v.lengthSec != null && <span>{v.lengthSec}s long</span>}
          {watchPct != null && <span><Clock size={11} style={{ verticalAlign: 'middle' }} /> avg watch {rec.avgWatchSec}s ({watchPct}%)</span>}
          {v.createdTime && <span>posted {fmtDate(v.createdTime)}</span>}
          {rec && rec.analyzedAt && <span>scored {fmtDate(rec.analyzedAt)}{rec.commentsRead ? ` from ${rec.commentsRead} comments` : ''}</span>}
        </div>

        {busy && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8 }}><Loader2 size={12} className="spin" style={{ verticalAlign: 'middle', marginRight: 4 }} />Reading comments and watch time…</div>}
        {!busy && !rec && <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>Not scored yet.</div>}

        {!busy && rec && (
          <div style={{ marginTop: 8, fontSize: 12.5, color: '#333', lineHeight: 1.5 }}>
            <div><strong>Why it went viral:</strong> {rec.whyViral}</div>
            <div><strong>{rec.verdict === 'skip' ? 'Why skip:' : 'Why boost:'}</strong> {rec.reason}</div>
            {rec.verdict !== 'skip' && (
              <div style={{ marginTop: 10, background: '#f7f9fb', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12 }}>
                  <span><Rocket size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--orange)' }} /><strong>Boost:</strong> {rec.objective}{rec.dailyBudget ? `, $${rec.dailyBudget}/day for ${rec.days} days ($${rec.dailyBudget * rec.days} test)` : ''}</span>
                  {rec.link && <span><ExternalLink size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /><strong>Send clicks to:</strong> {rec.link}</span>}
                </div>
                {rec.capture && <div style={{ fontSize: 12, marginTop: 6 }}><Target size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--orange)' }} /><strong>Save these audiences:</strong> {rec.capture}</div>}
                {rec.retarget && rec.retarget.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3 }}>Supporting videos to build for the retarget</div>
                    <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                      {rec.retarget.map((r, i) => <li key={i} style={{ marginBottom: 2 }}>{r}</li>)}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {v.permalink && <a href={v.permalink} target="_blank" rel="noreferrer" style={{ ...ghostBtn, textDecoration: 'none', textAlign: 'center' }}>Open on Facebook</a>}
        <button style={ghostBtn} disabled={disabled} onClick={onRescore}>{rec ? 'Re-score' : 'Score'}</button>
      </div>
    </div>
  );
}

// Boost a post: one click builds the paid boost (ThruPlay) on an Instagram post plus
// the two retarget audiences, all PAUSED, then "Go live" switches it on. Runs under the
// staff session (meta-boost launch-boost / go-live).
const inputStyle = { width: '100%', padding: '7px 9px', fontSize: 12.5, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'Montserrat', color: 'var(--navy)', boxSizing: 'border-box', marginTop: 3 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500)' };
const BOOST_DEFAULTS = {
  postUrl: '', label: 'Snow crab fork',
  link: 'https://crabplace.com/crab-shellfish/crab/specialty/snow-crab/?utm_source=meta&utm_medium=paid_social&utm_campaign=snowcrab-fork-boost&utm_content=ig-fork-video',
  utmCampaign: 'snowcrab-fork-boost', productSlug: 'snow-crab', dailyBudget: 25, days: 5,
};

async function boostCall(body) {
  const token = getAccessToken();
  const r = await fetch('/.netlify/functions/meta-boost', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  const text = await r.text();
  try { return JSON.parse(text); } catch { return { error: `Server returned ${r.status}` }; }
}

function BoostLauncher() {
  const [f, setF] = useState(BOOST_DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [live, setLive] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function launch() {
    setBusy(true); setRes(null); setLive(null);
    setRes(await boostCall({ action: 'launch-boost', ...f }));
    setBusy(false);
  }
  async function goLive() {
    setBusy(true);
    setLive(await boostCall({ action: 'go-live', campaignId: res.campaignId, adsetId: res.adsetId, adId: res.adId }));
    setBusy(false);
  }
  const ready = res && res.campaignId && res.adsetId && res.adId;
  const allLive = live && live.results && live.results.every((r) => r.ok);

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header"><span>Boost a post</span></div>
      <div className="card-body" style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12, lineHeight: 1.5 }}>
          Boosts an Instagram post for video views (ThruPlay) with a tracked Shop Now button, and saves two retarget audiences: people who watched it and people who clicked through. Everything is built <strong>paused</strong> so you can check it, then Go live.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <label style={labelStyle}>INSTAGRAM POST LINK<input style={inputStyle} value={f.postUrl} onChange={set('postUrl')} placeholder="blank = the newest video post" /></label>
          <label style={labelStyle}>NAME<input style={inputStyle} value={f.label} onChange={set('label')} /></label>
          <label style={labelStyle}>DAILY BUDGET ($)<input style={inputStyle} type="number" value={f.dailyBudget} onChange={set('dailyBudget')} /></label>
          <label style={labelStyle}>DAYS<input style={inputStyle} type="number" value={f.days} onChange={set('days')} /></label>
          <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>BUTTON LINK (with UTMs)<input style={inputStyle} value={f.link} onChange={set('link')} /></label>
          <label style={labelStyle}>UTM CAMPAIGN (audience B)<input style={inputStyle} value={f.utmCampaign} onChange={set('utmCampaign')} /></label>
          <label style={labelStyle}>PRODUCT URL WORD (audience B)<input style={inputStyle} value={f.productSlug} onChange={set('productSlug')} /></label>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={primaryBtn} disabled={busy} onClick={launch}>
            {busy && !res
              ? <><Loader2 size={13} className="spin" style={{ verticalAlign: 'middle', marginRight: 4 }} />Building…</>
              : <><Rocket size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Build boost (paused)</>}
          </button>
          {ready && !allLive && <button style={{ ...primaryBtn, background: 'var(--orange)' }} disabled={busy} onClick={goLive}>Go live: ${f.dailyBudget}/day for {f.days} days</button>}
          {res && res.adsManager && <a href={res.adsManager} target="_blank" rel="noreferrer" style={{ ...ghostBtn, textDecoration: 'none' }}>Open in Ads Manager</a>}
        </div>
        {res && res.error && <div style={{ fontSize: 12, color: '#c33', marginTop: 10 }}>{res.error}</div>}
        {res && res.steps && (
          <div style={{ marginTop: 12, fontSize: 12 }}>
            {res.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '3px 0' }}>
                {s.ok ? <CheckCircle2 size={14} color="#1a7a43" style={{ flexShrink: 0, marginTop: 1 }} /> : <XCircle size={14} color="#c33" style={{ flexShrink: 0, marginTop: 1 }} />}
                <span><strong>{s.label}</strong>{s.id ? ` (${s.id})` : ''}{s.caption ? `: "${s.caption}"` : ''}{s.error ? `: ${s.error}` : ''}</span>
              </div>
            ))}
          </div>
        )}
        {live && (
          <div style={{ marginTop: 10, fontSize: 12, color: allLive ? '#1a7a43' : '#c33' }}>
            {allLive ? 'Live. The boost starts within a few minutes.' : `Not all switched on: ${JSON.stringify(live.results || live.error)}`}
          </div>
        )}
      </div>
    </div>
  );
}
